import React, { useState } from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Dialog from '@material-ui/core/Dialog';
import FlatButton from 'material-ui/FlatButton';
import Row from 'components/ui/Row';
import Col from 'components/ui/Col';
import Spacer from 'components/ui/Spacer';
import Lottie from 'react-lottie';
import CtaPrimary from 'components/ui/CtaPrimary';

import { getTranslatedString } from 'helpers/i18n';
import { colors } from 'helpers/ColorHelper';
import { AccentColor } from 'helpers/AccentColorHelper';
import withMobileDialog from '@material-ui/core/withMobileDialog';

import MediaQuery from 'react-responsive';
import { media } from 'helpers/MediaHelper';
import successAnimationData from 'assets/success.json';
import errorAnimationData from 'assets/error.json';

const StyledDialogTitle = styled(DialogTitle)`
  padding: 24px;
`;

const StyledForm = styled.form`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const StyledTitle = styled.h3`
  margin: 0px;
  font-size: 24px;
  font-family: 'Circular-Bold';
  font-weight: bold;
  color: #333333;
  text-align: center;
  ${media.mobile`
    text-align: left;
  `};
`;

export const LEGAL_NOTICES_FORM = 'legal_notices_form';
export const LEGAL_NOTICES_SUCCESS = 'legal_notices_success';
export const LEGAL_NOTICES_ERROR = 'legal_notices_error';

const DigitalLegalNotices = ({
  fullScreen,
  dialogOpen,
  toggleDialog,
  toggleLegalNotices,
  getProperties,
  propertyId,
  canSendLegalNotices,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [legalNoticesFormStep, setLegalNoticesFormStep] = useState(LEGAL_NOTICES_FORM);

  const handleSubmit = (data) => {
    setIsSubmitting(true);
    toggleLegalNotices(data, propertyId)
      .then((resp) => {
        setIsSubmitting(false);
        setLegalNoticesFormStep(LEGAL_NOTICES_SUCCESS);
        getProperties();
      })
      .catch((error) => {
        setIsSubmitting(false);
        setLegalNoticesFormStep(LEGAL_NOTICES_ERROR);
        console.error(error);
      });
  };

  const handleClose = () => {
    setLegalNoticesFormStep(LEGAL_NOTICES_FORM);
    toggleDialog(propertyId);
  };

  const translatePrefix = `landlordPortal.properties.digitalLegalNoticesDialog.${
    canSendLegalNotices ? 'disabled' : 'enabled'
  }`;

  return (
    <Dialog
      disableBackdropClick
      fullScreen={fullScreen}
      open={dialogOpen}
      onClose={() => {
        toggleDialog(propertyId);
      }}
      classes={{
        paper: 'form-dialog',
      }}
    >
      {legalNoticesFormStep === LEGAL_NOTICES_FORM && (
        <Formik
          initialValues={{
            // Toggle the current value - if enabled, form should disable and vice versa
            canSendLegalNotices: !canSendLegalNotices,
          }}
          onSubmit={(values) => handleSubmit(values)}
        >
          {({ handleChange, submitForm }) => (
            <LegalNoticesForm
              canSendLegalNotices={canSendLegalNotices}
              handleChange={handleChange}
              submitForm={submitForm}
              handleClose={handleClose}
              isSubmitting={isSubmitting}
            />
          )}
        </Formik>
      )}
      {[LEGAL_NOTICES_SUCCESS, LEGAL_NOTICES_ERROR].includes(legalNoticesFormStep) && (
        <LegalNoticesResponse
          handleClose={handleClose}
          legalNoticesFormStep={legalNoticesFormStep}
          translatePrefix={translatePrefix}
        />
      )}
    </Dialog>
  );
};

export default withMobileDialog()(DigitalLegalNotices);

const LegalNoticesForm = ({ canSendLegalNotices, submitForm, handleClose, isSubmitting }) => {
  const translatedPrefix = `landlordPortal.properties.digitalLegalNoticesDialog.${
    canSendLegalNotices ? 'disable' : 'enable'
  }`;
  const title = getTranslatedString(`${translatedPrefix}Title`);
  const buttonText = getTranslatedString(translatedPrefix);
  const descriptionText = getTranslatedString(`${translatedPrefix}Description`);

  return (
    <StyledForm>
      <StyledDialogTitle>
        <StyledTitle>{title}</StyledTitle>
      </StyledDialogTitle>
      <DialogContent>
        <FormVerbiage description={descriptionText} />
      </DialogContent>
      <DialogActions
        style={{ justifyContent: 'space-between', marginRight: '24px', marginLeft: '24px' }}
      >
        <FormButtons
          handleClose={handleClose}
          submitForm={submitForm}
          isSubmitting={isSubmitting}
          buttonText={buttonText}
        />
      </DialogActions>
    </StyledForm>
  );
};

const FormVerbiage = ({ description }) => (
  <Spacer mb={1.7}>
    <Row gutter={16}>
      <MediaQuery query="(min-device-width: 750px)">
        <Col span={24} style={{ textAlign: 'center', padding: '0px 22px 0px' }}>
          {description}
        </Col>
      </MediaQuery>
      <MediaQuery query="(max-device-width: 750px)">
        <Col span={24}>{description}</Col>
      </MediaQuery>
    </Row>
  </Spacer>
);

const FormButtons = ({ handleClose, submitForm, isSubmitting, buttonText }) => (
  <React.Fragment>
    <FlatButton onClick={handleClose} id="close-button" style={{ color: AccentColor() }}>
      {getTranslatedString('close')}
    </FlatButton>
    <FlatButton
      onClick={() => submitForm()}
      id="submit-button"
      style={{
        color: 'white',
        backgroundColor: isSubmitting ? colors.LIGHT_GRAY : AccentColor(),
      }}
    >
      {buttonText}
    </FlatButton>
  </React.Fragment>
);

const StyledResponseContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin: 50px;
`;

const StyledResponseHeading = styled.div`
  font-size: 28px;
  font-family: 'Circular-Black';
`;

const LegalNoticesResponse = ({ handleClose, legalNoticesFormStep, translatePrefix }) => {
  let animationData = successAnimationData;
  let heading = getTranslatedString(`${translatePrefix}.success`);
  let verbiage = getTranslatedString(`${translatePrefix}.description`);

  if (legalNoticesFormStep === LEGAL_NOTICES_ERROR) {
    const errorPrefix = 'landlordPortal.properties.digitalLegalNoticesDialog.error';
    animationData = errorAnimationData;
    heading = getTranslatedString(`${errorPrefix}.unableToProcess`);
    verbiage = getTranslatedString(`${errorPrefix}.encounteredError`);
  }
  const defaultOptions = {
    loop: false,
    autoplay: true,
    animationData: animationData,
    rendererSettings: {
      preserveAspectRatio: 'xMidYMid slice',
    },
  };

  return (
    <StyledResponseContainer>
      <Lottie options={defaultOptions} height={150} width={150} />
      <StyledResponseHeading>{heading}</StyledResponseHeading>
      <Spacer mb={1} style={{ textAlign: 'center' }}>
        <div>{verbiage}</div>
      </Spacer>
      <CtaPrimary onClick={handleClose}>{getTranslatedString('close')}</CtaPrimary>
    </StyledResponseContainer>
  );
};
